/** Pure helpers for the Search screen of the Visitors Desk. */
import type { SearchRow, SearchView, SiteSearchRow } from '../api/visitorsDesk'
import type { Tone } from './deskFormat'
import { resultsText } from './deskFormat'
import { fmtInt } from './format'

export interface ViewButton { id: string; label: string; views: SearchView[] }

/** One button per question; Rising / falling, Brand vs topic and Discover & News show two views together. */
export const VIEW_BUTTONS: ViewButton[] = [
  { id: 'close', label: 'Close to page one', views: ['close'] },
  { id: 'ctr_gap', label: 'Ranked, not clicked', views: ['ctr_gap'] },
  { id: 'move', label: 'Rising / falling', views: ['rising', 'falling'] },
  { id: 'brand', label: 'Brand vs topic', views: ['brand', 'topic'] },
  { id: 'feeds', label: 'Discover & News', views: ['discover', 'news'] },
]

/** Why the term list is shorter than the tile totals; empty when the share is unknown. */
export function coverageText(visibleShare: number | null | undefined): string {
  if (visibleShare === null || visibleShare === undefined) return ''
  return `Google hides rare search terms for privacy, so this list covers about ${Math.round(visibleShare * 100)}% of search clicks. Totals in the tiles above are complete.`
}

/** Google position change: lower is better, so a negative change is "up". */
export function positionChange(c: { change: number | null | undefined }): { text: string; tone: Tone } {
  if (c.change === null || c.change === undefined) return { text: '—', tone: 'flat' }
  const n = Math.round(c.change * 10) / 10
  if (n === 0) return { text: '0', tone: 'flat' }
  return n < 0 ? { text: `▲ ${Math.abs(n)}`, tone: 'up' } : { text: `▼ ${n}`, tone: 'down' }
}

/** Position now minus position then, or null when either is missing. */
export function rowPositionChange(r: SearchRow): number | null {
  return r.position === null || r.prev_position === null ? null : r.position - r.prev_position
}

/** `+96 clicks` / `−31 clicks`; the rising and falling views change in clicks. */
export function clicksChange(n: number | null | undefined): { text: string; tone: Tone } {
  if (n === null || n === undefined) return { text: '—', tone: 'flat' }
  if (n === 0) return { text: '0 clicks', tone: 'flat' }
  return n > 0 ? { text: `+${fmtInt(n)} clicks`, tone: 'up' } : { text: `−${fmtInt(-n)} clicks`, tone: 'down' }
}

/** Site-search results in words, so zero results never rely on colour alone. */
export function siteResultsText(r: Pick<SiteSearchRow, 'results' | 'results_display' | 'zero_results'>): { text: string; zero: boolean } {
  const shown = resultsText(r.results_display)
  if (r.results_display === '0') return { text: '0 · nothing found', zero: true }
  if (r.zero_results > 0) return { text: `${shown} · ${fmtInt(r.zero_results)}× nothing found`, zero: true }
  return { text: shown, zero: false }
}
